import { Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { ChartDesigner, ChartState } from '@mode/legacy/types-app-state';
import { ChartDataTypes, ChartTypes, FlamingoTypes, InterActions } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { combineLatest, filter, map, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { makeVizExplorerObservables } from '~/observables/viz-explorer.observables';
import { AppStore } from '~/types/main.types';
import { ExplorerActionType } from '../../../../actions/chart-designer/explorer.actions';
import { MixableBaseController } from '../../../../common/component';
import { ChartExportHelper } from '../../../../lib/chart-export-helper';
import { ChartDesignerSelectors } from '../../../../selectors/designer.selectors';
import { ChartExplorerService } from '../../../../services/chart-designer/chart-explorer.service';
import { FeatureToggleService } from '../../../../services/feature-toggle-service';
import { FlamingoService } from '../../../../services/flamingo-service';
import { ReportApiService } from '../../../../services/report-api-service';
import { VizExplorerObservables } from '../../../../types/viz-designer.types';

class VizExplorerController extends MixableBaseController {
  static $inject = [
    '$scope',
    'ChartExplorerService',
    'RxStoreService',
    'ReportApiService',
    'FlamingoService',
    'FeatureToggleService',
    'ToastService',
    'DataViewFacade',
    'FlamingoFacade',
    'ChartFacade',
    '$state',
    'AnalyticsService',
    'FeatureFlagsFacade',
  ];

  // bindings
  withSidebar: boolean;
  exploreEnabled: boolean;
  token: string;
  chartDataExportEnabled: boolean;
  chartType: ChartTypes.ChartType;

  // local state
  private readOnly: boolean;
  private objectContext = ChartTypes.ObjectType.Exploration;
  private sidebarOpen = true;
  observables: VizExplorerObservables;
  encoding$: Observable<Encoding>;
  format$: Observable<ChartTypes.ChartFormat>;
  fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
  chartState$: Observable<ChartState>;
  model$: Observable<FlamingoTypes.ModeModel>;
  view$: Observable<FlamingoTypes.ModeView>;
  queryToken$: Observable<string>;
  activeTableColumn$: Observable<string>;
  queryExists$: Observable<boolean>;
  isViewLoaded$: Observable<boolean>;
  datasetForCopy$: Observable<string>;

  drilldownEnabled$: Observable<boolean>;
  vizToolbarEnabled$: Observable<boolean>;
  showResults$: Observable<boolean>;
  planLoading$: Observable<boolean>;
  invalid$: Observable<boolean>;

  showChartPane$: Observable<boolean>;
  chartPaneView$: Observable<ChartDesigner.PaneView>;
  showDataPane$: Observable<boolean>;
  dataPaneView$: Observable<ChartDesigner.PaneView>;
  chartPaneHeight$: Observable<number>;
  dataPaneHeight$: Observable<number>;
  chartData$: Observable<ChartDataTypes.Dataset>;
  isChart$: Observable<boolean>;
  isTable$: Observable<boolean>;
  isBigValue$: Observable<boolean>;
  unappliedChangesCount$: Observable<number>;
  isImportedDataset$: Observable<boolean>;

  constructor(
    private $scope: angular.IScope,
    private explorerService: ChartExplorerService,
    private rxStore: AppStore,
    private reportApiService: ReportApiService,
    flamingoService: FlamingoService, // for side effects
    private featureService: FeatureToggleService,
    private alertService: ToastService,
    private dataViewFacade: DataViewFacade,
    private flamingoFacade: FlamingoFacade,
    private chartFacade: ChartFacade,
    private $state,
    private analyticsService: AnalyticsService
  ) {
    super();

    this.readOnly = true;

    this.drilldownEnabled$ = featureService.featureDrilldownEnabled$;
    this.vizToolbarEnabled$ = featureService.featureVizToolbarEnabled$;
  }

  $onInit() {
    this.observables = makeVizExplorerObservables(
      this.rxStore,
      this.dataViewFacade,
      this.flamingoFacade,
      this.chartFacade
    );

    this.isImportedDataset$ = this.observables.switchViewToken$.pipe(
      switchMap((token) => this.dataViewFacade.createEnhancedDatasetResultBySwitchViewToken(token)),
      map((selectedDatasetResult) => !selectedDatasetResult.isLocal)
    );

    // Assignments for convenience
    this.queryToken$ = this.observables.queryToken$;
    this.encoding$ = this.observables.encoding$;
    this.format$ = this.observables.format$;
    this.fieldFormats$ = this.observables.fieldFormats$;
    this.model$ = this.observables.model$;
    this.chartState$ = this.observables.chartState$;
    this.chartData$ = this.observables.chartData$;
    this.queryExists$ = this.observables.queryExists$;
    this.isViewLoaded$ = this.observables.isViewLoaded$;
    this.datasetForCopy$ = this.observables.datasetForCopy$;
    this.unappliedChangesCount$ = this.observables.unappliedChangesCount$;
    this.view$ = this.observables.view$;
    this.planLoading$ = this.observables.planLoading$;

    this.activeTableColumn$ = this.rxStore.select(ChartDesignerSelectors.getActiveTableColumn);
    this.isChart$ = this.observables.chartType$.pipe(map((type) => type !== 'table' && type !== 'bigValue'));
    this.isTable$ = this.observables.chartType$.pipe(map((type) => type === 'table'));
    this.isBigValue$ = this.observables.chartType$.pipe(map((type) => type === 'bigValue'));
    this.observables.chartType$.pipe(takeUntil(this.destroyed$)).subscribe((type) => (this.chartType = type));

    // This is `vizDesigner` feature state. Observables are created here for the time being.
    const designer$ = this.rxStore.select(ChartDesignerSelectors.getPanes).pipe(filter((d) => d != null));
    this.chartPaneView$ = designer$.pipe(map((d) => d.chartPane.view));
    this.dataPaneView$ = designer$.pipe(map((d) => d.dataPane.view));
    this.chartPaneHeight$ = designer$.pipe(map((d) => d.chartPane.height));
    this.dataPaneHeight$ = designer$.pipe(map((d) => d.dataPane.height));
    this.showChartPane$ = this.chartPaneView$.pipe(
      map((view) => view === ChartDesigner.PaneView.Regular || view === ChartDesigner.PaneView.Maximized)
    );
    this.showDataPane$ = this.dataPaneView$.pipe(
      map((view) => view === ChartDesigner.PaneView.Regular || view === ChartDesigner.PaneView.Maximized)
    );

    // Keep track of unsaved changes to trigger save exploration modal on exit.
    this.chartState$.pipe(withLatestFrom(this.view$), takeUntil(this.destroyed$)).subscribe(([change, view]) => {
      // ephemeral calcs will have a token starting with ref_
      const ephemeralCalcsExist = view.attrs.some((a) => a.token && a.token.startsWith('ref_'));

      if (this.$state.includes('**.explore') && this.$state.includes('reportShow')) {
        this.$state.current.data.hasChanges = change.canUndo || ephemeralCalcsExist;
      }
    });

    this.showResults$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planSucceeded$.pipe(startWith(false))),
      map(([canRender, success]) => canRender && success)
    );

    this.invalid$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planFailed$.pipe(startWith(false))),
      map(([canRender, failed]) => !canRender || failed)
    );

    // Report API Service embed handlers
    this.reportApiService
      .makeObservable(this.observables.token$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ action, chartToken }) => {
        if (action.type === InterActions.Type.ContextClick) {
          switch (action.item.type) {
            case InterActions.ContextItemType.ViewData:
              const target = action.item.target;
              this.$scope.$evalAsync(() => {
                this.rxStore.dispatch({
                  chartToken,
                  target,
                  type: ExplorerActionType.ViewChartData,
                });
              });
              break;
          }
        }
      });
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  minimizeChartPane() {
    this.explorerService.minimizeChartPane();
  }

  maximizeChartPane() {
    this.explorerService.maximizeChartPane();
  }

  maximizeDataPane() {
    this.explorerService.maximizeDataPane();
  }

  minimizeDataPane() {
    this.explorerService.minimizeDataPane();
  }

  copySuccess() {
    this.alertService.success('Copied to clipboard');
  }

  export(title: string, chartData: ChartDataTypes.Dataset) {
    if (chartData) {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.Exploration,
        action: 'Export chart data to CSV',
        resource: AnalyticsEvent.Context.Report,
        chartType: this.chartType,
      });

      ChartExportHelper.saveAs(ChartExportHelper.makeCsv(chartData), title);
    }
  }
}

export const VizExplorerComponent: angular.IComponentOptions = {
  bindings: {
    token: '<',
    withSidebar: '<',
    exploreEnabled: '<',
    chartDataExportEnabled: '<',
  },
  template: `
    <div ng-if="(ctrl.queryExists$ | async:this) === true && (ctrl.isViewLoaded$ | async:this) === true" class="viz-explorer-body">
      <viz-toolbar
        observables="ctrl.observables"
        read-only="ctrl.readOnly"
        model="ctrl.model$ | async:this"
        view-name="ctrl.queryToken$ | async:this"
        encoding="ctrl.encoding$ | async:this"
        format="ctrl.format$ | async:this"
        field-formats="ctrl.fieldFormats$ | async:this"
        selected-chart="ctrl.chartState$ | async:this"
        chart-service="ctrl.explorerService"
        on-open-sidebar="ctrl.onOpenSidebar()"
        toggle-sidebar="ctrl.toggleSidebar()"
        sidebar-open="ctrl.sidebarOpen"
        active-table-column="ctrl.activeTableColumn$ | async:this"
        ng-if="ctrl.vizToolbarEnabled$ | async:this"
        object-context="ctrl.objectContext"
        is-imported-dataset="ctrl.isImportedDataset$ | async:this"
        viz-token="ctrl.token"
      ></viz-toolbar>
      <div class="vegas-sidebar" ng-if="ctrl.sidebarOpen && ctrl.exploreEnabled">
        <viz-settings
          observables="ctrl.observables"
          chart-service="ctrl.explorerService"
          object-context="ctrl.objectContext"
          read-only="true">
        </viz-settings>
      </div>
      <div class="designer-container">
        <div class="chart-designer" ng-class="{ 'with-sidebar': (ctrl.sidebarOpen && ctrl.exploreEnabled) }">
          <tiled-panes
            ng-if="!(ctrl.isTable$ | async:this)"
            vertical="true"
            adjustable="false"
            show-primary="ctrl.showChartPane$ | async:this"
            hide-primary-header="true"
            show-secondary="ctrl.showDataPane$ | async:this"
            secondary-collapsed-title="Chart Data"
            primary-height="ctrl.chartPaneHeight$ | async:this"
            secondary-height="ctrl.dataPaneHeight$ | async:this"
            on-primary-maximized="ctrl.maximizeChartPane()"
            on-primary-minimized="ctrl.minimizeChartPane()"
            on-secondary-maximized="ctrl.maximizeDataPane()"
            on-secondary-minimized="ctrl.minimizeDataPane()">

            <primary-header></primary-header>

            <primary-pane>
              <designer-renderer
                ng-if="ctrl.isChart$ | async:this"
                read-only="ctrl.readOnly"
                observables="ctrl.observables"
                chart-service="ctrl.explorerService"
                is-view-page="false"
                object-context="ctrl.objectContext"
              ></designer-renderer>
              <big-value-designer-renderer
                ng-if="ctrl.isBigValue$ | async:this"
                read-only="ctrl.readOnly"
                observables="ctrl.observables"
                chart-service="ctrl.explorerService"
                is-view-page="false"
                object-context="ctrl.objectContext"
              ></big-value-designer-renderer>
            </primary-pane>

            <secondary-header>
              <div  class="results-button-container">
                <capra-button
                  ng-if="ctrl.showResults$ | async:this"
                  text="Download"
                  variant="secondary"
                  color="neutral"
                  size="small"
                  (clicked)="ctrl.export(ctrl.chartTitle$ | async:this, ctrl.chartData$ | async:this)"
                  [disabled]="ctrl.unappliedChangesCount$ | async:this"
                >
                </capra-button>
                <mode-clipboard-compat
                  tooltip-content="Copy results to clipboard"
                  [disabled]="!(ctrl.showResults$ | async:this) || (ctrl.unappliedChangesCount$ | async:this)"
                  [clipboard-text]="ctrl.datasetForCopy$ | async:this"
                  tag="capra-button"
                  text="Copy"
                >
                </mode-clipboard-compat>
              </div>
            </secondary-header>

            <secondary-pane>
              <designer-empty-table
                ng-if="(!(ctrl.chartData$ | async: this) || (ctrl.unappliedChangesCount$ | async:this)) && (ctrl.showDataPane$ | async:this)"
                pane-view="ctrl.dataPaneView$ | async:this"
                chart-type="ctrl.chartType$ | async:this">
              </designer-empty-table>
              <div class="view-data-modal-table-container"
                ng-if="(ctrl.chartData$ | async:this) && !(ctrl.unappliedChangesCount$ | async:this)">
                <pivot-results-data-grid
                  ng-if="(ctrl.showResults$ | async:this) && !(ctrl.unappliedChangesCount | async:this)"
                  id="view-data-grid"
                  dataset="ctrl.chartData$ | async:this">
                </pivot-results-data-grid>
                <mode-table-loading
                  ng-if="(ctrl.planLoading$ | async:this) || (ctrl.invalid$ | async:this)" [with-animation]="ctrl.planLoading$ | async:this"
                ></mode-table-loading>
              </div>
            </secondary-pane>
          </tiled-panes>
          <designer-renderer
            ng-if="ctrl.isTable$ | async:this"
            read-only="ctrl.readOnly"
            observables="ctrl.observables"
            chart-service="ctrl.explorerService"
            is-view-page="false"
            object-context="ctrl.objectContext"
          ></designer-renderer>
        </div>
      </div>
      <calc-editor-modal is-exploring="true"></calc-editor-modal>
    </div>
    <div ng-if="!ctrl.token || ((ctrl.queryExists$ | async:this) === false)" class="exploration-error-container">
      <mode-simple-error legacy-icon="icon-broken-viz">
        Sorry, this exploration can't be displayed. The underlying dataset has been deleted.
      </mode-simple-error>
    </div>
  `,
  controller: VizExplorerController,
  controllerAs: 'ctrl',
};
